<template>
  <div
    id="vueproduct"
    :key="upid"
    :class="
      bemBlockMod({
        modal: context === CONTEXT_MODAL,
      })
    "
    :data-get-content-asset="getContentAssetUrl"
  >
    <BaseBackButton
      v-if="context === CONTEXT_ROUTER_VIEW && !shouldHideExternalBackButton"
    />
    <div
      class="product-detail product-wrapper product-detail-vue"
      :data-context="context"
      :data-pid="product.id"
    >
      <BaseProductDataForSfra :product="product" />
      <meta itemprop="sku" :content="product.id" />
      <meta itemprop="mpn" :content="product.id" />
      <meta
        v-if="product.longDescription"
        itemprop="description"
        :content="product.longDescription"
      />
      <meta itemprop="brand" content="BIMBA Y LOLA" />
      <div
        ref="imagesContainer"
        :class="
          bemElement(
            isBreakpointDesktop ? 'images-container' : 'carousel-container'
          )
        "
      >
        <div v-if="!hasImages" :class="bemElement('pre-placeholder')">
          <span :class="[bemElement('pic'), 'img']" />
        </div>
        <BaseImage
          v-for="(imageSet, imageIndex) in productImageSets"
          :key="imageIndex"
          :class="bemElement('pic')"
          :src="getDefaultImageUrlFromProductImageSet(imageSet)"
          :alt="getDefaultImageAltFromProductImageSet(imageSet)"
          :loading="imageIndex < 1 || areCriticalImagesLoaded ? 'eager' : null"
          :placeholder="getPlaceholderImageUrlFromProductImageSet(imageSet)"
          :sizes="imageAttributeSizes.PDP_GALLERY"
          :srcset="getSrcsetFromProductImageSet(imageSet)"
          @image-click="(event) => handleProductImageClick(event, imageIndex)"
          @load="handleImageLoad(imageIndex)"
        />
        <BaseCarousel
          v-if="$windowWidth < 575 && hasImages"
          :class="bemElement('carousel')"
          :items="productImageSets"
          @carousel-click="
            (event, currentIndex) =>
              handleProductImageClick(event, currentIndex)
          "
        >
          <template #default="slotProps">
            <BaseImage
              :ref="`carouselItem${slotProps.index}`"
              :class="slotProps.classname"
              :src="getDefaultImageUrlFromProductImageSet(slotProps.item)"
              :alt="getDefaultImageAltFromProductImageSet(slotProps.item)"
              :image-classes="bemElement('image')"
              :loading="
                slotProps.index === 0 || areCriticalImagesLoaded
                  ? 'eager'
                  : null
              "
              :placeholder="
                getPlaceholderImageUrlFromProductImageSet(slotProps.item)
              "
              :sizes="imageAttributeSizes.PDP_GALLERY"
              :srcset="getSrcsetFromProductImageSet(slotProps.item)"
              @load="handleImageLoad(slotProps.index)"
            />
          </template>
        </BaseCarousel>
      </div>
      <div :class="bemElement('text-container')">
        <div ref="infoContainer" :class="bemElement('sticky-container')">
          <div
            class="interaction--loading-content"
            :class="{ 'interaction-active': !loading }"
            data-interaction="interaction--loading-content"
          >
            <div class="loading-content">
              <BaseSpinner small grey />
            </div>
            <div class="loaded-content">
              <div :class="bemElement('sticky-child-container')">
                <div :class="bemElement('title-wrapper')">
                  <h1 :class="bemElement('title')" itemprop="name">
                    {{ product.productName }}
                  </h1>
                </div>
              </div>
              <p
                v-if="product.longDescription"
                :class="bemElement('description')"
                v-html="product.longDescription"
              />
              <BaseProductSlider
                v-if="individualProducts.length > 0"
                :class="bemElement('products')"
                :products="individualProducts"
                :tile-context="productSet"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <BaseProductSlider
      v-if="recommendedProducts.length > 0"
      :class="bemElement('recommendations')"
      :heading="recommendationsHeading"
      :products="recommendedProducts"
      :wrap-items="true"
      :tile-context="pdpRecommendedSlider"
      :hide-product-names="recommendationsDisplay.hideProductNames"
      :hide-prices="recommendationsDisplay.hidePrices"
      :hide-discount="recommendationsDisplay.hideDiscount"
      :hide-tags="recommendationsDisplay.hideTags"
      :is-recommendation-from-einstein="isRecommendationFromEinstein"
      :recommender-name="recommendationsRecommenderName"
    />
    <TheProductDetailsZoom
      :class="{
        'is-visible': isZoomOpen,
      }"
      :is-visible="isZoomOpen"
      :images="productImageSets"
      :loading="areGalleryImagesLoaded ? 'eager' : null"
      :zoom-requirements="zoomRequirements"
      @close-click="closeZoom(true)"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EventBusConfig from '../config/eventBusConfig';
import bemClassesMixin from '../mixins/bemClassesMixin';
import { productImageSetMixin } from '../mixins/productImageSetMixin';
import { productBaseMixin } from '../mixins/productPropBaseMixin';
import sfraProductMixin from '../mixins/sfraProductMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';
import BaseBackButton from './BaseBackButton.vue';
import BaseCarousel from './BaseCarousel.vue';
import BaseImage from './BaseImage.vue';
import BaseProductDataForSfra from './BaseProductDataForSfra.vue';
import BaseProductSlider from './BaseProductSlider.vue';
import BaseSpinner from './BaseSpinner.vue';
import TheProductDetailsZoom from './TheProductDetailsZoom.vue';
import { getters } from '../store';
import getFrontConfig from '../../common/getFrontConfig';
import pageDataMixin from '../mixins/pageDataMixin';
import pdpDualScrollMixin from '../mixins/pdpDualScrollMixin';
import productRecommendationsMixin from '../mixins/productRecommendationsMixin';
import { PAGE_TYPES } from '../../../../../config/analytics';

const {
  productImages: {
    imageSets: { PDP },
  },
  responsive: { bp_575: bp575 },
  urls: { getContentAssetUrl },
  vue: {
    BaseProductTile: {
      context: { productSet, pdpRecommendedSlider },
    },
    TheProductDetails: {
      context: { modal: CONTEXT_MODAL, routerView: CONTEXT_ROUTER_VIEW },
    },
  },
} = getFrontConfig();

const SIZES = {
  PDP_GALLERY: '(min-width: 1024px) 37.5vw, (min-width: 768px) 66.6vw, 100vw',
  PDP_ZOOM: '100vw',
};

export default {
  name: 'TheProductSetDetails',

  components: {
    BaseBackButton,
    BaseCarousel,
    BaseImage,
    BaseProductDataForSfra,
    BaseProductSlider,
    BaseSpinner,
    TheProductDetailsZoom,
  },

  mixins: [
    bemClassesMixin,
    productBaseMixin,
    productImageSetMixin,
    productRecommendationsMixin,
    sfraProductMixin,
    windowResizeMixin,
    pageDataMixin,
    pdpDualScrollMixin,
  ],

  props: {
    addToCartUrl: {
      type: String,
      required: true,
    },
    context: {
      type: String,
      validator: (prop) => [CONTEXT_MODAL, CONTEXT_ROUTER_VIEW].includes(prop),
      default: CONTEXT_ROUTER_VIEW,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      CONTEXT_MODAL,
      CONTEXT_ROUTER_VIEW,
      dataChangeBreakpoint: bp575,
      getContentAssetUrl,
      imageAttributeSizes: SIZES,
      isZoomOpen: false,
      loadedImagesIndexes: [],
      zoomRequirements: {},
      productSet,
      pdpRecommendedSlider,
    };
  },

  computed: {
    ...mapGetters({
      shouldHideExternalBackButton: getters.header.shouldHideExternalBackButton,
    }),
    /** Critical images are those which should be loaded first.
     * - Mobile: 1st image
     * - Desktop: 1st and 2nd images
     * @returns {boolean} - true or false
     */
    areCriticalImagesLoaded() {
      if (this.isBreakpointMobile) {
        return this.loadedImagesIndexes.indexOf(0) > -1;
      }
      return (
        this.loadedImagesIndexes.indexOf(0) > -1 &&
        this.loadedImagesIndexes.indexOf(1) > -1
      );
    },
    areGalleryImagesLoaded() {
      return (
        this.loadedImagesIndexes.indexOf(this.productImageSets.length - 1) > -1
      );
    },
    hasImages() {
      return this.productImages['pdp-large']?.length >= 1;
    },
    individualProducts() {
      return this.product?.individualProducts || [];
    },
    productImageSets() {
      return this.getProductImageSets(PDP);
    },
  },

  watch: {
    loading() {
      this.pageTitle = this.product?.productName || '';
      this.emitAnalyticsData();
    },
  },

  mounted() {
    this.pageType = PAGE_TYPES.product_look;
  },

  beforeDestroy() {
    window.removeEventListener('popstate', this.closeZoom);
  },

  methods: {
    closeZoom() {
      if (!this.isZoomOpen) return;
      this.isZoomOpen = false;
      this.$emit('zoomclose');
      this.$eventBus.$emit(EventBusConfig.events.TheProductDetails.zoomClosed);
    },
    getZoomPercentileY(event, index) {
      const clickedItem =
        this.$refs[`carouselItem${index}`]?.$el || event.currentTarget;
      const clickedElementTop = clickedItem?.getBoundingClientRect()
        ? clickedItem.getBoundingClientRect().top + window.scrollY
        : 0;
      const relativeTopToImage =
        event.pageY >= clickedElementTop ? event.pageY - clickedElementTop : 0;
      return clickedItem?.offsetHeight
        ? relativeTopToImage / clickedItem.offsetHeight
        : relativeTopToImage;
    },
    handleImageLoad(index) {
      this.loadedImagesIndexes.push(+index);
    },
    handleProductImageClick(event, index = null) {
      this.openZoom(index, this.getZoomPercentileY(event, index));
    },
    openZoom(ordinal = 0, percentileY = 0) {
      if (this.isZoomOpen) return;
      this.zoomRequirements = {
        ordinal,
        percentileY,
      };
      this.isZoomOpen = true;
      this.$emit('zoomopen');
      this.$eventBus.$emit(
        EventBusConfig.events.TheProductDetails.zoomOpened,
        this.zoomRequirements
      );
      // trigger when any navigation event
      window.addEventListener('popstate', this.closeZoom, {
        once: true,
        passive: true,
      });
    },
    emitAnalyticsData() {
      if (!this.loading) {
        this.$eventBus.$emit(
          EventBusConfig.events.TheProductDetails.loaded,
          this.product
        );
      }
    },
  },
};
</script>
